import React from 'react';

interface ProgressBarProps {
    value: number;
    max: number;
    className?: string;
    showPercentage?: boolean;
    animated?: boolean;
    color?: 'primary' | 'success' | 'warning' | 'error';
}

export function ProgressBar({
    value,
    max,
    className = '',
    showPercentage = false,
    animated = true,
    color = 'primary'
}: ProgressBarProps) {
    const percentage = Math.min(100, Math.max(0, (value / max) * 100));

    const colorClasses = {
        primary: 'from-primary to-primary/60',
        success: 'from-green-500 to-green-400',
        warning: 'from-yellow-500 to-yellow-400',
        error: 'from-red-500 to-red-400'
    };

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <div className="relative flex-1 h-2 bg-muted/50 rounded-full overflow-hidden">
                <div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r ${colorClasses[color]} ${animated ? 'transition-all duration-1000 ease-out' : ''}`}
                    style={{ width: `${percentage}%` }}
                >
                    {animated && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                    )}
                </div>
            </div>
            {showPercentage && (
                <span className="text-xs font-medium text-muted-foreground min-w-[3ch]">
                    {Math.round(percentage)}%
                </span>
            )}
        </div>
    );
}

interface CircularProgressProps {
    value: number;
    max: number;
    size?: number;
    strokeWidth?: number;
    className?: string;
}

export function CircularProgress({
    value,
    max,
    size = 60,
    strokeWidth = 6,
    className = ''
}: CircularProgressProps) {
    const percentage = Math.min(100, Math.max(0, (value / max) * 100));
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;

    return (
        <div className={`relative inline-flex items-center justify-center ${className}`}>
            <svg width={size} height={size} className="transform -rotate-90">
                {/* Background circle */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="none"
                    className="text-muted/30"
                />
                {/* Progress circle */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className="text-primary transition-all duration-1000 ease-out"
                    strokeLinecap="round"
                />
            </svg>
            <span className="absolute text-[10px] font-bold">
                {Math.round(percentage)}%
            </span>
        </div>
    );
}
